import { useCallback, useRef } from 'react';
import { FILTER_DROPDOWN_MIN_SPACE } from 'src/components/star-grid/filterDropdownConstants';
import { filterDropdownSpace, positionFilterDropdown } from 'src/components/star-grid/utils/filterDropdownPosition';

const observeDropdown = (trigger: HTMLElement, dropdown: HTMLElement): (() => void) => {
  const update = () => positionFilterDropdown(trigger, dropdown);
  const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
  observer?.observe(trigger);
  observer?.observe(dropdown);
  window.addEventListener('scroll', update, true);
  window.addEventListener('resize', update);
  window.visualViewport?.addEventListener('resize', update);
  return () => {
    observer?.disconnect();
    window.removeEventListener('scroll', update, true);
    window.removeEventListener('resize', update);
    window.visualViewport?.removeEventListener('resize', update);
  };
};

/**
 * Places an open filter dropdown against its trigger. Returns a CALLBACK ref for a marker node
 * rendered inside the panel.
 *
 * A callback ref rather than a `useLayoutEffect`, because the panel mounts LATER than the component
 * that owns this hook. `HierarchyFilterDropdown` calls it while closed, so on mount there was no
 * marker and no `[role="dialog"]`, the effect bailed, and — keyed only on `controlId` — it never ran
 * again when the panel opened. `positionFilterDropdown` therefore never set
 * `--filter-dropdown-top`, which left `inset-block-start: var(--filter-dropdown-top)` invalid at
 * computed-value time, resolving to `auto`; a `position: fixed` element with auto insets renders at
 * its STATIC position, i.e. up at the top of the page over the report title instead of under the
 * chip. Keying on the node's arrival is what makes "open" the trigger rather than "mounted".
 */
export const useFilterDropdownPosition = (controlId: string) => {
  const detach = useRef<(() => void) | undefined>(undefined);
  return useCallback(
    (marker: HTMLSpanElement | null) => {
      detach.current?.();
      detach.current = undefined;
      if (!marker) return;
      const trigger = document.getElementById(controlId);
      const dropdown = marker.closest<HTMLElement>('[role="dialog"]');
      if (!trigger || !dropdown) return;
      // Too little room below to be usable: bring the trigger into view first so the panel opens
      // downward against it rather than being sized to a few pixels.
      if (filterDropdownSpace(trigger) < FILTER_DROPDOWN_MIN_SPACE && typeof trigger.scrollIntoView === 'function')
        trigger.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      positionFilterDropdown(trigger, dropdown);
      detach.current = observeDropdown(trigger, dropdown);
    },
    [controlId]
  );
};
